import { FC, useState } from 'react';
import { Tag, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApplyCoupon } from '../../../hooks/useCart';

interface CouponInputProps {
    cartId: string;
    onApplySuccess: (couponData: any) => void;
    onClear: () => void;
    appliedCouponCode?: string | null;
}

export const CouponInput: FC<CouponInputProps> = ({
    cartId,
    onApplySuccess,
    onClear,
    appliedCouponCode
}) => {
    const { t } = useTranslation('shop');
    const [isOpen, setIsOpen] = useState(false);
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);

    const { mutate: applyCoupon, isPending } = useApplyCoupon();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!code.trim()) {
            setError('Please enter a coupon code');
            return;
        }

        applyCoupon(
            { code: code.trim(), cartId },
            {
                onSuccess: (data) => {
                    if (data.valid) {
                        onApplySuccess(data);
                        setIsOpen(false);
                        setCode('');
                    } else {
                        // Backend usually throws 400 for invalid, but standardizes valid: false response format 
                        // just in case we hit 200 with invalid status.
                        setError('Invalid coupon');
                    }
                },
                onError: (err: any) => {
                    setError(err.response?.data?.message || 'Invalid or expired coupon');
                }
            }
        );
    };

    // If a coupon is already applied, show the success state
    if (appliedCouponCode) {
        return (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-700">
                    <Tag size={16} />
                    <span className="text-sm font-bold">Code applied: {appliedCouponCode}</span>
                </div>
                <button
                    onClick={onClear}
                    className="text-emerald-600 hover:text-emerald-800 p-1 hover:bg-emerald-100 rounded-lg transition-colors"
                    aria-label="Remove coupon"
                >
                    <X size={16} />
                </button>
            </div>
        );
    }

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary transition-colors py-2"
            >
                <Tag size={16} />
                <span>{t('cart.haveDiscountCode')}</span>
            </button>
        );
    }

    // Expanded input state
    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Tag size={16} className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value.toUpperCase());
                            setError(null);
                        }}
                        placeholder="Enter code..."
                        className={`w-full pl-9 pr-4 py-2.5 bg-gray-50 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all uppercase placeholder:normal-case ${error ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-primary'
                            }`}
                        disabled={isPending}
                    />
                </div>
                <button
                    type="submit"
                    disabled={isPending || !code.trim()}
                    className="px-5 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center min-w-[80px]"
                >
                    {isPending ? <Loader2 size={16} className="animate-spin" /> : t('cart.apply')}
                </button>
            </div>
            {error && (
                <p className="text-xs text-red-500 font-medium pl-1">{error}</p>
            )}
        </form>
    );
};
